import { StopBlock, OperatorRecord } from "../../types";
import { i18nLanguage } from "i18n";
import { DistanceUnit } from "shared/types";
import { formatDistance } from "shared/utils/formatDistance";
import { getLocalizedString } from "../../utils/getLocalizedString";

type CarouselStopCardProps = {
  stop: StopBlock;
  operator: OperatorRecord;
  language: i18nLanguage;
  markerNumber: number;
  isActive: boolean;
  distanceToNext?: { meters: number; seconds: number };
  unit?: DistanceUnit;
  onClick?: () => void;
};

const formatAddress = (
  address?: {
    line1?: string | null;
    city?: string | null;
    subdivision?: string | null;
  } | null,
) => {
  const parts = [address?.line1, address?.city, address?.subdivision].filter(Boolean);
  return parts.join(", ");
};

const formatDuration = (seconds: number) => {
  const mins = Math.round(seconds / 60);
  if (mins < 1) return "<1 min";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
};

export const CarouselStopCard = ({
  stop,
  operator,
  language,
  markerNumber,
  isActive,
  distanceToNext,
  unit = "metric",
  onClick,
}: CarouselStopCardProps) => {
  const operatorName = operator?.name
    ? getLocalizedString(operator.name, language)
    : null;
  const address = formatAddress(operator?.contact?.address);

  const businessType = operator?.type
    ? operator.type
        .replace(/_/g, " ")
        .replace(/business type/i, "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const stopTag = stop.overnight ? "Overnight" : businessType;

  const distanceLabel = distanceToNext
    ? [
        distanceToNext.meters > 0 ? formatDistance(distanceToNext.meters, unit) : "",
        distanceToNext.seconds > 0 ? formatDuration(distanceToNext.seconds) : "",
      ]
        .filter(Boolean)
        .join(" · ") + " to next stop"
    : null;

  return (
    <div className="tripper-carousel__card-column">
      <button
        type="button"
        className={`tripper-carousel__card${isActive ? " tripper-carousel__card--active" : ""}`}
        data-item-id={stop._id}
        onClick={onClick}
      >
        <div className="tripper-carousel__card-header">
          <span className="tripper-carousel__badge">{markerNumber}</span>
          {stopTag && <span className="tripper-carousel__tag">{stopTag}</span>}
        </div>
        {operatorName && (
          <span className="tripper-carousel__name">{operatorName}</span>
        )}
        {address && (
          <span className="tripper-carousel__address">{address}</span>
        )}
      </button>
      <span className="tripper-carousel__distance-label">
        {distanceLabel ?? "\u00A0"}
      </span>
    </div>
  );
};
